import { IStartupRegisterable, IStartupRegistry, IStartupState } from 'app/startup/registry/types';
import { WaitableSingleUseDOMEvent } from 'app/shared/utilities/waitable_single_use_dom_event';
import DebugService from 'app/shared/debug.service';
import { PromisedTimeout } from 'app/shared/utilities/PromisedTimeout';

const log = (...args) => DebugService.log('StartupRegistry', ...args);

export class StartupRegistry implements IStartupRegistry {
    _stateByService: Map<IStartupRegisterable, IStartupState>;
    _startEvent: WaitableSingleUseDOMEvent;
    _name: string;

    /**
     * A StartupRegistry service can be used to control the startup part of the app lifecycle; it
     * should be coupled with the use of one or more StartupRegisterables, but it can be used on it's
     * own (and instances that want to be involved in the startup organisation should call .register
     * and .setReady when appropriate)
     *
     * @param registryName optional name to help with event identification
     */
    constructor(registryName: string = null) {
        this._name = registryName;
        this._stateByService = new Map<IStartupRegisterable, IStartupState>();
        this._startEvent = new WaitableSingleUseDOMEvent();
    }

    /**
     * Register a given service
     * @param service
     */
    public register(service: IStartupRegisterable): Date {
        if (this._stateByService.has(service)) {
            return;
        }

        const state = {
            isRegistered: true,
            registeredAt: new Date(),
            isReady: false,
            readyAt: null,
            ready: new WaitableSingleUseDOMEvent(),
        } as IStartupState;

        this._stateByService.set(service, state);

        return state.registeredAt;
    }

    /**
     * This function is mainly here for debugging in case something goes wrong and a service
     * does not become ready.
     * Usage: window.services.<registryName>.getNotReadyList()
     */
    public getNotReadyList(): IStartupRegisterable[] {
        const result = [];
        this._stateByService.forEach((state, service, map) => {
            if (!state.isReady) {
                result.push(service);
            }
        });
        return result;
    }

    /**
     * Get the registered / ready state of a given service
     * @param service
     */
    public getState(service: IStartupRegisterable): IStartupState {
        if (!this._stateByService.has(service)) {
            console.error(
                `Failed to get status for service ${service}; service not known to StartupRegistry!`,
            );
            return;
        }

        return this._stateByService.get(service);
    }

    /**
     * Set the ready state of a given service
     * @param service
     */
    public setReady(service: IStartupRegisterable): void {
        if (!this._stateByService.has(service)) {
            console.error(
                `Failed to mark service ${service} as ready; service not known to StartupRegistry!`,
            );
            return;
        }

        const state = this._stateByService.get(service);
        if (state.isReady) {
            return;
        }

        state.readyAt = new Date();
        state.isReady = true;

        state.ready.dispatch();
    }

    /**
     * Wait for the given service to be ready
     * @param service
     */
    public async waitForReady(service: IStartupRegisterable): Promise<void> {
        await this._startEvent.wait();
        if (!this._stateByService.has(service)) {
            console.error(
                `Failed to wait for service ${service} to be ready; service not known to StartupRegistry!`,
            );
            return;
        }

        const state = this._stateByService.get(service);

        return state.ready.wait();
    }

    /**
     * Wait for all services to be ready
     */
    public async waitForAllReady(): Promise<void[]> {
        log(`Registry ${this._name} waitForAllReady`);
        await this._startEvent.wait();
        log(`Registry ${this._name} started`);

        const allReady = Array.from(this._stateByService.values()).map((x) => {
            return x.ready.wait();
        });
        log(`Registry ${this._name} ready`);
        // TODO: not using .allSettled because our tests don't like it (because of the Node version)
        return Promise.all(allReady);
    }

    /**
     * Wait for all services to be ready OR a timeout occurs
     * Timeout defaults to 10000 seconds, i.e. should be enough to allow one
     * collection fetch cycle to complete
     */
    public async waitForAllReadyWithTimeout(timeoutMs: number = 10000): Promise<any> {
        log(`Registry ${this._name} waitForAllReadyWithTimeout`);
        await this._startEvent.wait();
        log(`Registry ${this._name} started`);

        const allReady = Array.from(this._stateByService.values()).map((x) => {
            return x.ready.wait();
        });
        log(`Registry ${this._name} ready`);

        // TODO: not using .allSettled because our tests don't like it (because of the Node version)
        return Promise.race([Promise.all(allReady), new PromisedTimeout(timeoutMs).promise]);
    }

    /**
     * Call this after _all_ of the IStartupRegisterable have been added
     * to the registry.
     *
     * Calls IStartupRegisterable.start() on all managed objects
     *
     * IMPORTANT: waitForAllReady() will not resolve until startAll() is called.
     */
    startAll(): void {
        log(`${this._name} startAll() called`);
        this._stateByService.forEach((state, service, map) => {
            service.start();
        });
        log(`${this._name} start() called on each service`);
        this._startEvent.dispatch().catch((e) => {
            console.error('Error dispatching start up registry start event', e);
        });
        log(`${this._name} dispatch() called on start event`);
    }

    /**
     * Wait for startAll() to be called
     */
    async waitForStartAll(): Promise<void> {
        return this._startEvent.wait();
    }
}
